import { useRef, useState } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { FormProvider } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { useAppSelector } from 'src/app/store/hooks';
import { PayerOrganizationRead } from 'src/common/external/bambi-api/bambiApi';
import { FormSubmitButton } from 'src/common/FormSubmitButton';
import { PassengerInfoFormSection } from 'src/features/add-trip/PassengerInfoFormSection';
import { PricingFormSection } from 'src/features/add-trip/Pricing';
import { ServiceDetailsFormSection } from 'src/features/add-trip/ServiceDetails';
import { TripBookedBySection } from 'src/features/add-trip/TripBookedBySection';
import { PassengerFormModal } from 'src/features/passenger/PassengerForm/PassengerForm';

import {
  setIsEditingPassenger,
  setSelectedPayer,
  setShowTripSubscriptionModal,
} from '../trip-subscriptions.slice';
import SubscriptionDetailsSection from './SubscriptionDetailsSection/SubscriptionDetailsSection';
import { TripSubscriptionFormError } from './TripSubscriptionFormError';
import { useFormManagement } from './useFormManagement';
import { usePassengerSelection } from './usePassengerSelection';

export default function TripSubscriptionForm() {
  const dispatch = useDispatch();
  const errorRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  const selectedTripSubscription = useAppSelector(
    (state) => state.tripSubscription.selectedTripSubscription
  );

  const selectedPayer = useAppSelector(
    (state) => state.tripSubscription.selectedPayer
  );

  const {
    selectedPassenger,
    isEditingPassenger,
    onPassengerSelected,
    onPassengerEdit,
    handlePassengerAddEdit,
  } = usePassengerSelection();

  const { methods, isSubmitting, onFormSubmit, formattedError } =
    useFormManagement(setError, errorRef);

  const onPayerSelected = (payer: PayerOrganizationRead | null) => {
    dispatch(setSelectedPayer(payer));
  };

  return (
    <>
      <FormProvider {...methods}>
        <form
          className="h-[100vh] w-[100vw] overflow-scroll"
          onSubmit={methods.handleSubmit(onFormSubmit)}
          data-testid="trip-subscription-form"
        >
          <div className="flex h-full flex-col justify-between divide-y divide-gray-200">
            <div className="flex flex-col gap-y-4 px-4 pb-4 pt-2 sm:px-6">
              <h1 className="flex items-center gap-2 text-xl font-bold">
                {selectedTripSubscription?.id ? 'Edit' : 'Add'} Trip
                Subscription
              </h1>
              <div ref={errorRef}>
                <TripSubscriptionFormError
                  error={error}
                  formattedError={formattedError}
                />
              </div>
              <PassengerInfoFormSection
                selectedPassenger={selectedPassenger}
                onPassengerSelected={onPassengerSelected}
                onPassengerEdit={onPassengerEdit}
              />
              <PricingFormSection
                showPayerLinkSwitch={false}
                selectedPayer={selectedPayer}
                onPayerSelected={onPayerSelected}
              />
              <ServiceDetailsFormSection />
              <TripBookedBySection selectedPayer={selectedPayer} />
              <SubscriptionDetailsSection />
            </div>

            <div className="flex flex-shrink-0 justify-between gap-x-1 bg-white px-4 py-4 sm:px-6">
              <div></div>
              <div className="flex flex-shrink-0 items-center justify-end">
                {isSubmitting && <LoadingIndicator />}
                <Button
                  variant="ghost"
                  dataTestId="trip-subscription-modal-cancel-button"
                  onClick={() => {
                    dispatch(setSelectedPayer(null));
                    dispatch(setShowTripSubscriptionModal(false));
                  }}
                >
                  Cancel
                </Button>
                <FormSubmitButton
                  isSubmitting={isSubmitting}
                  buttonName="Save"
                />
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
      <PassengerFormModal
        open={isEditingPassenger}
        setOpen={(open) => {
          dispatch(setIsEditingPassenger(open));
        }}
        passenger={selectedPassenger || undefined}
        handlePassengerAddEdit={(passenger) => {
          handlePassengerAddEdit(passenger);
        }}
      />
    </>
  );
}
